import React, { ComponentType, useLayoutEffect } from 'react'
import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router-dom'
import { useApi, useLogic, useStore } from '../../../hooks/storeHook'
import { parseStoreAccountPathFromUrl } from '../../../hooks/routeHooks'
import { ProductsTable } from '../../widgets/productsTable'
import { IntegrationType } from '../../../server/mpsklad_core/Entity/IntegrationType'
import { ozonColumns, wbColumns, ymColumns, msColumns } from '../../shared/lib/columns'
import { OzonProductsTableData, WbProductsTableData, YandexMarketProductsTableData } from '../../../store/productStore'
import { OzonProductModel } from '../../../server/mpsklad_core/Models/OzonProductModel'
import { OzonProductColumnId } from '../../../types/ozonColumns'
import { WbProductModel } from '../../../server/mpsklad_core/Models/WbProductModel'
import { WbProductColumnId } from '../../../types/wbColumns'
import { YandexMarketProductModel } from '../../../server/mpsklad_core/Models/YandexMarketProductModel'
import { YandexMarketProductColumnId } from '../../../types/yandexMarketColumns'
import { MoySkladProductModel } from '../../../server/mpsklad_core/Models/MoySkladProductModel'
import { MoySkladProductColumnId } from '../../../types/moySkladColumns'
import { createUniversalColumns } from './lib/tableUtils'
import { NavLink } from 'react-router-dom'
import { mpCardRoutes } from '../../../common/mpCardRoutes'
import { withProductsPolling } from '../../widgets/productsTable/withPollingOptions'
import { ProductsTableProps } from '../../widgets/productsTable/productsTable'
import { integrationKeyByType } from '../../../types/integrationTypeUtils'


export const Main = observer(() => {
  const location = useLocation()
  const accountRoute = parseStoreAccountPathFromUrl(location.pathname)
  const {
    productStore,
    hiddenMyProductColumnIds,
    mpcardStore: {clearSelectedRows},
    unsetHomeNavAccount,
    setNavFirstAccount
  } = useStore()
  const {tryGetAccount} = useLogic()
  const classes = useStyles()
  const api = useApi()

  useLayoutEffect(() => {
    clearSelectedRows()

    if (accountRoute.integration != null && accountRoute.accountId != null) {
      setNavFirstAccount(accountRoute.integration, accountRoute.accountId)
    }
  }, [accountRoute.integration, accountRoute.accountId, clearSelectedRows, setNavFirstAccount])

  const handleUnsetAccount = () => {
    unsetHomeNavAccount()
  }

  const AddAccountButton = <NavLink
    to={mpCardRoutes.settings.new.route({integration: 'Wildberries'})} onClick={handleUnsetAccount}
    className={classes.link}>Добавить
    магазин</NavLink>

  const integrationKey =
    accountRoute.integration != null ? integrationKeyByType[accountRoute.integration] : undefined

  const account =
    accountRoute.accountId != null && integrationKey != null
    ? tryGetAccount(accountRoute.accountId, integrationKey)
    : undefined

  if (!account) {
    return <Box className={classes.wrapper}>
      {AddAccountButton}</Box>
  }

  switch (account.integrationType) {
    case IntegrationType.MoySklad: {
      const products = productStore.createMoySkladProductsTable(account.id)

      return (
        <Box className={classes.wrapper}>
          <Typography variant="h5" gutterBottom className={classes.title}>
            Управление товарами
          </Typography>
          <ProductsTable
            products={products}
            columns={createUniversalColumns<MoySkladProductColumnId, MoySkladProductModel>(
              msColumns,
              hiddenMyProductColumnIds
            )}
          />
        </Box>
      )
    }

    case IntegrationType.Ozon: {
      const products = productStore.createOzonProductsTable(account.id)

      const ProductsWithPolling =
        withProductsPolling<
          OzonProductModel,
          OzonProductColumnId,
          ProductsTableProps<OzonProductsTableData, OzonProductModel, OzonProductColumnId>
        >(
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          ProductsTable as ComponentType<ProductsTableProps<OzonProductsTableData, OzonProductModel, OzonProductColumnId>>,
          {
            accountId: account.id,
            reloadProduct: api.product.getOzonCard
          })

      return (
        <Box className={classes.wrapper}>
          <Typography variant="h5" gutterBottom className={classes.title}>
            Управление товарами
          </Typography>
          <ProductsWithPolling
            products={products}
            columns={createUniversalColumns<OzonProductColumnId, OzonProductModel>(ozonColumns,
              hiddenMyProductColumnIds)}
          />
        </Box>
      )
    }

    case IntegrationType.Wildberries: {
      const products = productStore.createWbProductsTable(account.id)

      const ProductsWithPolling =
        withProductsPolling<
          WbProductModel,
          WbProductColumnId,
          ProductsTableProps<WbProductsTableData, WbProductModel, WbProductColumnId>
        >(
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          ProductsTable as ComponentType<ProductsTableProps<WbProductsTableData, WbProductModel, WbProductColumnId>>,
          {
            accountId: account.id,
            reloadProduct: api.product.getWbCard
          })

      return (
        <Box className={classes.wrapper}>
          <Typography variant="h5" gutterBottom className={classes.title}>
            Управление товарами
          </Typography>
          <ProductsWithPolling
            products={products}
            columns={createUniversalColumns<WbProductColumnId, WbProductModel>(wbColumns,
              hiddenMyProductColumnIds)}
          />
        </Box>
      )
    }

    case IntegrationType.YandexMarket: {
      const products = productStore.createYandexMarketProductsTable(account.id)

      const ProductsWithPolling =
        withProductsPolling<
          YandexMarketProductModel,
          YandexMarketProductColumnId,
          ProductsTableProps<YandexMarketProductsTableData, YandexMarketProductModel, YandexMarketProductColumnId>
        >(
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          ProductsTable as ComponentType<ProductsTableProps<YandexMarketProductsTableData, YandexMarketProductModel, YandexMarketProductColumnId>>,
          {
            accountId: account.id,
            reloadProduct: api.product.getYandexMarketCard
          })

      return (
        <Box className={classes.wrapper}>
          <Typography variant="h5" gutterBottom className={classes.title}>
            Управление товарами
          </Typography>
          <ProductsWithPolling
            products={products}
            columns={createUniversalColumns<YandexMarketProductColumnId, YandexMarketProductModel>(ymColumns,
              hiddenMyProductColumnIds)}
          />
        </Box>
      )
    }

    default:
      throw new Error('Unsupported integration type')
  }
})

const useStyles = makeStyles(() => ({
  title: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: '120%',
    letterSpacing: '0%'
  },
  wrapper: {
    width: '100%',
    maxWidth: '100vw',
    padding: '37px 26px',
    boxSizing: 'border-box'
  },
  link: {
    textDecoration: 'none',
    color: '#727272',
    textTransform: 'uppercase'
  }
}))
